import { useMemo, useState } from 'react';

import { CompanyGroupCard } from './CompanyGroupCard';
import { groupByCompany, isActionable } from './grouping';
import { GuidedFlowDialog } from './GuidedFlowDialog';
import { ItemDialogs } from './ItemDialogs';
import { amountDueOf } from './outstanding';
import { useGetOutstandingItemsQuery } from './outstanding.api';
import { OutstandingHero } from './OutstandingHero';
import { OutstandingSkeleton } from './OutstandingSkeleton';
import { OutstandingEmpty, OutstandingError } from './OutstandingStates';
import { useItemDialogs } from './useItemDialogs';

/**
 * Everything the director owes a signature or a payment on.
 *
 * Built as a task list, not a data table. The screen this replaces put a search
 * field, a type filter, a date range, four sortable columns and a paginator in
 * front of a director who, on the median day, has three items — every one of
 * those controls asked them to operate a database before it would tell them
 * what they owed. None of them survive: the list is one director's outstanding
 * work, it is short, and it is already ordered by urgency.
 *
 * What replaces them is the hero and the guided flow. The page opens on the
 * amount due and a single button that walks every item to completion, and the
 * items themselves are grouped under the company they belong to, because that
 * is the unit a director is actually responsible for.
 */
export const OutstandingList = () => {
  const { data, isLoading, isError, refetch } = useGetOutstandingItemsQuery({});
  const dialogs = useItemDialogs();

  // `undefined` is closed; a string is the item to open the queue at, and the
  // empty string means "start from the top" — distinct from not being open.
  const [flowStartAt, setFlowStartAt] = useState<string | undefined>(undefined);

  const items = useMemo(() => data ?? [], [data]);

  const groups = useMemo(() => groupByCompany(items), [items]);

  const { amountDue, taskCount, waitingCount } = useMemo(
    () =>
      items.reduce(
        (totals, item) => {
          const actionable = isActionable(item);
          return {
            amountDue: totals.amountDue + amountDueOf(item),
            taskCount: totals.taskCount + (actionable ? 1 : 0),
            waitingCount: totals.waitingCount + (actionable ? 0 : 1),
          };
        },
        { amountDue: 0, taskCount: 0, waitingCount: 0 },
      ),
    [items],
  );

  if (isError) return <OutstandingError onRetry={() => void refetch()} />;

  if (isLoading) return <OutstandingSkeleton />;

  if (items.length === 0) return <OutstandingEmpty />;

  return (
    <div className="flex flex-col gap-4">
      {/* Always rendered, including when nothing is actionable. A director whose
          items are all with co-directors still arrives asking "what do I owe?",
          and the hero answering "nothing needs your attention" IS that answer —
          hiding it left the question to be inferred from a list of rows the
          director cannot act on. The hero drops its button in that state. */}
      <OutstandingHero
        amountDue={amountDue}
        taskCount={taskCount}
        waitingCount={waitingCount}
        onStart={() => {
          setFlowStartAt('');
        }}
      />

      <div className="flex flex-col gap-3">
        {groups.map((group) => (
          <CompanyGroupCard
            key={group.companyId}
            group={group}
            onAction={dialogs.open}
            onStart={(item) => {
              setFlowStartAt(item.id);
            }}
          />
        ))}
      </div>

      {flowStartAt !== undefined && (
        <GuidedFlowDialog
          items={items}
          startAt={flowStartAt === '' ? undefined : flowStartAt}
          onClose={() => {
            setFlowStartAt(undefined);
          }}
        />
      )}

      <ItemDialogs state={dialogs} />
    </div>
  );
};
